import React from 'react';
import DeviceList from './DeviceList';
import Modal from '../components/Modal';
import { addDeviceApi, updateWaterThreshold, setDevInputsApi } from '../api/api';
import AlertAccent from '../components/AlertAccent';
import DeviceAdder from './DeviceAdder';
import LoadingSpinner from '../components/LoadingSpinner';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { empty } from '../regex';
import { useDevicesStore, useObjectsStore, useUserStore } from '../stores';
import { generateControllerSerialNumber, generateSerialNumber } from '../utils/generateSerialNumber';
import { useWaterTariffStore } from '../features/Devices/waterTariffs/use-water-tariff-store';
import { ERROR_TEXT, validationRules } from '../constants';
import TitleWithIcon from '../components/TitleWithIcon';
import { initialDevs } from '../features/Devices/constants/constants';

const Devices = () => {
  const { user } = useUserStore();
  const { objects } = useObjectsStore();
  const { getGroupDevices } = useDevicesStore();

  const [showAddModal, setShowAddModal] = React.useState(false);
  const [showWarningModal, setShowWarningModal] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [objectId, setObjectId] = React.useState({});
  const [validationErrors, setValidationErrors] = React.useState([]);

  React.useEffect(() => {
    const get = async () => {
      await getGroupDevices();
      setLoading(true);
      setObjectId({ ...objectId, object_id: objects?.[0].id });
      setLoading(false);
    };
    get();
  }, []);

  const devToAddDefault = {
    device_type: initialDevs[0].device_type,
    device_sub_type: initialDevs[0].sub[0].device_type,
    manufacturer: 'Meridian',
    description: 'Ванная',
    serial_number: '',
    user_id: user.id,
    output_type: initialDevs[0].sub[0].output_type[0].key,
  };

  const [deviceToAdd, setDeviceToAdd] = React.useState(devToAddDefault);
  const [threshold, setThreshold] = React.useState(null); // порог расхода воды
  const [deviceInputs, setDeviceInputs] = React.useState([]); // каналы для контроллера
  const isSaveInputs = deviceToAdd.device_type === 'controller' && deviceInputs && deviceInputs.length > 0;

  const { updateWaterTariffs } = useWaterTariffStore(); // тарифы

  const createThreshold = async (deviceId) => {
    if (deviceId && threshold) {
      const body = { device_id: deviceId, daily_threshold: Number(threshold) };
      await updateWaterThreshold(body, function (err) {
        toast.error(err.error, { hideProgressBar: true });
      });
      setThreshold(null);
    }
  };

  const addDevice = async () => {
    let device = { ...deviceToAdd, object_id: objectId?.object_id };

    if (device.device_type === 'controller') {
      const { serial_letters = '', serial_numbers = '', ...restPart } = device;
      const serial_number = generateControllerSerialNumber(serial_letters, serial_numbers);
      device = { ...restPart, serial_number };
    }

    // генерация серийного номера для датчика протечки:
    if (device.device_sub_type === 'sensor:water:leakage') {
      device.serial_number = generateSerialNumber();
    }

    // сохранение каналов:
    if (isSaveInputs) {
      device.children = deviceInputs;
    }

    if (validateDeviceToAdd(device)) {
      const newDevice = await addDeviceApi(
        device,
        () => {},
        function (err) {
          toast.error(err.error, { hideProgressBar: true });
        }
      );

      if (isSaveInputs && device.device_type === 'controller') {
        await setDevInputsApi(
          newDevice.id,
          deviceInputs,
          () => {},
          (err) => {
            toast.error(err.error, { hideProgressBar: true });
          }
        );
      }
      setShowAddModal(false);
      setDeviceInputs([]);
      await getGroupDevices();

      const type = newDevice?.type?.key;
      if (type === 'counter') {
        await createThreshold(newDevice?.id);
        await updateWaterTariffs(newDevice?.id);
      }
    }
  };

  const validateDeviceToAdd = (device) => {
    const errors = [];
    setValidationErrors(errors);
    if (empty.test(device.serial_number)) {
      errors.push({ error: ERROR_TEXT.SERIAL_NUMBER_EMPTY });
    }
    if (device.device_type === 'controller' && device.serial_number.length !== validationRules.SERIAL_NUMBER_LENGTH) {
      errors.push({ error: ERROR_TEXT.SERIAL_NUMBER_LENGTH });
    }
    if (empty.test(device.manufacturer)) {
      errors.push({ error: ERROR_TEXT.MANUFACTURER_EMPTY });
    }
    if (!device.description) {
      errors.push({ error: ERROR_TEXT.LOCATION_EMPTY });
    }
    return errors.length === 0;
  };

  const add = async () => {
    setDeviceToAdd(devToAddDefault);
    setDeviceInputs([]);
    setValidationErrors([]);
    setShowAddModal(true);
  };

  return (
    <div className="sm:flex min-h-screen items-start justify-center bg-gradient-to-r p-2 sm:p-4 from-[#ededed] to-[#dcdcdc]">
      {loading ? (
        <LoadingSpinner loading={true} />
      ) : (
        <>
          <div className="w-full max-w-sm sm:max-w-sm md:max-w-xl lg:max-w-2xl xl:max-w-4xl 2xl:max-w-5xl">
            <TitleWithIcon title={'Список устройств'} handler={() => (objects.length !== 0 ? add() : setShowWarningModal(true))} />
            <div>
              <DeviceList user={user} />
            </div>
          </div>

          <Modal
            showModal={showAddModal && user.verified_at}
            setShowModal={setShowAddModal}
            saveCb={addDevice}
            title="Добавление устройства"
            error={validationErrors}>
            <DeviceAdder
              setThreshold={setThreshold}
              threshold={threshold}
              setDevice={setDeviceToAdd}
              device={deviceToAdd}
              objects={objects}
              objectId={objectId}
              setObjectId={setObjectId}
              deviceInputs={deviceInputs}
              setDeviceInputs={setDeviceInputs}
            />
          </Modal>

          <Modal showModal={showWarningModal} setShowModal={setShowWarningModal} title="Добавление объекта">
            <AlertAccent title="Невозможно добавить устройство!" text="В начале добавьте хотя бы один объект" showAlert={true} />
          </Modal>

          <Modal showModal={showAddModal && !user.verified_at} setShowModal={setShowAddModal} title="Добавление объекта">
            <AlertAccent
              title="Невозможно добавить устройство!"
              text="Аккаунт не подтвержден. Для подтверждения аккаунта перейдите пожалуйста по ссылке, отправленной на Вашу почту"
              showAlert={true}
            />
          </Modal>
        </>
      )}
    </div>
  );
};

export default Devices;
